import { Separator } from '@radix-ui/react-dropdown-menu';
import { useMutation } from '@tanstack/react-query';
import {
  CheckIcon,
  EyeIcon,
  PlusCircleIcon,
  RefreshCcwIcon,
  RotateCcwIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { DialogDeleteRecordConfirmation } from '../pages/backoffice/dialogs/delete-record.jsx';
import { msCatalystApi } from '../../services/api/ms-catalyst-api.js';
import { cn } from '../../utils/cn';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export function DataTableToolbar({
  table,
  children,
  actions,
  showViewOptions = true,
  showRefetchOption = false,
  refetchFunc = () => null,
}) {
  const isFiltered = table?.getState().columnFilters.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-y-2 lg:flex-row lg:justify-between lg:gap-y-0">
        <div className="flex w-full flex-row flex-wrap gap-2 lg:w-auto lg:items-center lg:gap-x-2">
          {children}

          {isFiltered && (
            <Button
              variant="ghost"
              onClick={() => table.resetColumnFilters()}
              className="h-8 px-2 font-normal text-foreground/70 lg:px-3"
            >
              <RotateCcwIcon />
              Reset
            </Button>
          )}
        </div>
        <div className="flex flex-row flex-wrap gap-x-2 gap-y-2 lg:items-center lg:justify-end">
          {showRefetchOption ? (
            <Button onClick={() => refetchFunc()}>
              <RefreshCcwIcon />
              Atualizar
            </Button>
          ) : null}
          {showViewOptions ? <DataTableViewOptions table={table} /> : null}
        </div>
      </div>

      {actions && <DataTableMultiSelection table={table} actions={actions} />}
    </div>
  );
}

export function DataTableMultiSelection({ table, actions }) {
  const rows = table.getSelectedRowModel().rows;

  const mutationDelete = useMutation({
    mutationFn: async () => {
      const ids = rows.map((item) =>
        item.original.ROWID ? item.original.ROWID : item.original.id,
      );
      const { data: response } = await msCatalystApi.delete(
        `${actions.route}?ids=${ids.join()}`,
      );
      return response.data;
    },
    onSuccess: async () => {
      toast.success(`${rows.length} registo(s) eliminado(s) com sucesso!`);

      table.resetRowSelection();

      await actions.useQuery.refetch();
    },
    onError: (err) => {
      toast.error('Erro ao eliminar registo(s)');
    },
  });

  if (!actions.route || !actions.useQuery) {
    return null;
  }

  return (
    !!rows.length && (
      <div className="flex w-full items-center justify-between rounded-md border border-muted-foreground/35 bg-secondary p-4">
        <span className="text-sm">{rows.length} items selecionados</span>

        <div className="flex items-center gap-4">
          <DialogDeleteRecordConfirmation
            button={<Button variant="destructive">Eliminar</Button>}
            action={() => mutationDelete.mutateAsync()}
            message="Todos os registos selecionados seram eliminados
            permanentemente do sistema."
            alerts={false}
          />
        </div>
      </div>
    )
  );
}

export function DataTableViewOptions({ table }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <EyeIcon />
          Colunas
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        {table
          .getAllColumns()
          .filter(
            (column) =>
              typeof column.accessorFn !== 'undefined' && column.getCanHide(),
          )
          .map((column) => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
              >
                {column.columnDef.title}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DataTableFacetedFilter({
  column,
  title,
  search = true,
  options,
}) {
  const selectedValues = new Set(column?.getFilterValue());

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-10 border-dashed">
          <PlusCircleIcon />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm p-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm p-1 font-normal"
                  >
                    {selectedValues.size} selecionados
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm p-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          {search ? <CommandInput placeholder={title} /> : null}

          <CommandList>
            <CommandEmpty>Não foram encontrados resultados.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      column?.setFilterValue(
                        filterValues.length ? filterValues : undefined,
                      );
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className={cn('size-4')} />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 size-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>

            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => column?.setFilterValue(undefined)}
                    className="justify-center text-center"
                  >
                    Limpar Filtros
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
